


























































































































import Vue, { PropType } from "vue";

import {
  BreakerStatus,
  DynamicDocumentItem,
  DynamicDocumentItemData,
  InstallationModule,
  InstallationModuleData,
  Material,
  MechanicInstallation,
  Nullable,
  SerialNumber,
  StatusFlags
} from "@/types";
import { InstallationModuleStatus } from "@/store/modules/installation";
import { AxiosError } from "axios";

// A failed meter command sets the module status to "error", which the alert shows.
const shownInStatus = () => {};

export default Vue.extend({
  props: {
    installation: { type: Object as PropType<MechanicInstallation> },
    module: { type: Object as PropType<InstallationModule> },
    moduleData: Object as PropType<InstallationModuleData>,
    isActive: Boolean,
    item: Object as PropType<DynamicDocumentItem>,
    data: Object as PropType<DynamicDocumentItemData>
  },

  data() {
    return {
      showAlert: false,
      breakerStatusTimeout: null as Nullable<number>,
      breakerStatus: null as Nullable<BreakerStatus>,
      unregisterWarning: false
    };
  },

  computed: {
    flags(): StatusFlags {
      return this.module.flags || {};
    },

    serialnumbersComplete(): boolean {
      return (
        this.moduleData?.meterSerialNumbers?.length === 1 &&
        !!this.moduleData?.meterSerialNumbers?.every(
          sn => !!this.$store.getters["materials/meters/findItemBySn"](sn?.serialNumber)
        )
      );
    },

    hasAssociation(): boolean {
      return !!this.flags.hasAssociation;
    },

    hasBreaker(): boolean {
      if (this.module.type === "METER_MAAS-REGULAR") {
        const serialNumber = this.moduleData?.meterSerialNumbers?.[0]?.serialNumber;
        if (serialNumber) {
          return this.$store.getters["materials/meters/hasBreaker"](serialNumber);
        } else {
          return false;
        }
      }
      return true;
    },

    hasProductionTest(): boolean {
      return !!this.flags.hasProductionTest;
    },

    readyForAssociation(): boolean {
      return !this.isProcessing && !this.flags.hasAssociation && this.serialnumbersComplete;
    },

    readyForProductionTest(): boolean {
      return !this.isProcessing && !!this.flags.hasAssociation;
    },

    isProcessing(): boolean {
      return (
        this.status.status === "processing" &&
        (this.status.context !== "switch_breaker" || !this.breakerOn)
      );
    },

    isConnecting(): boolean {
      return this.isProcessing && this.status.context === "connect";
    },

    isDisconnecting(): boolean {
      return this.isProcessing && this.status.context === "disconnect";
    },

    isTesting(): boolean {
      return this.isProcessing && this.status.context === "production_test";
    },

    isSwitchingBreaker(): boolean {
      return this.isProcessing && this.status.context === "switch_breaker";
    },

    meterState(): string {
      if (this.isDisconnecting) {
        return "bezig met de meter deregistreren (dit kan een aantal minuten duren)";
      }

      if (this.isConnecting) {
        return "bezig met registreren (dit kan een aantal minuten duren)";
      }

      if (this.isTesting) {
        return "bezig met productietest (het uitvoeren van een productietest duurt maximaal 30 minuten.)";
      }

      if (this.isSwitchingBreaker) {
        return "bezig met aanzetten van meter (breaker AAN)";
      }

      if (this.flags.hasProductionTest) {
        return "productietest geslaagd";
      }

      if (this.breakerOn) {
        return "meter staat aan (breaker AAN)";
      }

      if (this.flags.hasAssociation) {
        return "gekoppeld";
      }

      return "niet gekoppeld";
    },

    breakerOn(): boolean {
      return !!this.breakerStatus?.breakerOn;
    },

    meter(): Material {
      return this.$store.getters["materials/meters/findItem"](this.moduleData?.meterId);
    },

    status(): InstallationModuleStatus {
      return this.$store.getters["installation/status"](this.installation.id, this.module.type);
    },

    serialNumber(): SerialNumber | undefined {
      return this.moduleData?.meterSerialNumbers?.[0];
    }
  },

  beforeDestroy() {
    window.clearTimeout(this.breakerStatusTimeout!);
  },

  watch: {
    isActive: {
      immediate: true,
      handler(val: boolean) {
        if (val) {
          this.checkBreakerStatus();
        } else {
          window.clearTimeout(this.breakerStatusTimeout!);
        }
      }
    },

    flags(val: StatusFlags, prev: StatusFlags) {
      if (!val || !prev) return;

      if (!prev.hasAssociation && val.hasAssociation) {
        this.$store.dispatch(
          "track",
          `meter with s/n: ${this.module.data?.meterSerialNumbers?.[0]?.serialNumber} has succesfully registered for installation: ${this.installation.id}`
        );
      }

      if (!prev.hasProductionTest && val.hasProductionTest) {
        this.$store.dispatch(
          "track",
          `meter with s/n: ${this.module.data?.meterSerialNumbers?.[0]?.serialNumber} has successful production test: ${this.installation.id}`
        );
      }
    },

    status(val: InstallationModuleStatus, prev: InstallationModuleStatus) {
      if (val.status !== "error" || !prev || prev.status === "error") return;

      switch (val.context) {
        case "production_test":
          this.$store.dispatch(
            "track",
            `Production test failed for meter with s/n: ${this.module.data?.meterSerialNumbers?.[0]?.serialNumber} for installation: ${this.installation.id} => ${val.error}`
          );
          break;
        case "connect":
          this.$store.dispatch(
            "track",
            `Associate meter failed for meter with s/n: ${this.module.data?.meterSerialNumbers?.[0]?.serialNumber} for installation: ${this.installation.id} => ${val.error}`
          );
          break;
        case "switch_breaker":
          this.$store.dispatch(
            "track",
            `Switch break failed for meter with s/n: ${this.module.data?.meterSerialNumbers?.[0]?.serialNumber} for installation: ${this.installation.id} => ${val.error}`
          );
          break;
      }
    },

    hasAssociation: {
      immediate: true,
      handler(hasAssociation: boolean) {
        if (
          hasAssociation &&
          this.data.value !== "breakerOn" &&
          this.data.value !== "hasAssociation"
        ) {
          this.data.value = "hasAssociation";
          this.$emit("save:document");
        }

        if (hasAssociation) {
          this.checkBreakerStatus();
        } else {
          window.clearTimeout(this.breakerStatusTimeout!);
        }
      }
    },

    breakerStatus: {
      immediate: true,
      handler(status: BreakerStatus, prev: BreakerStatus) {
        this.logBreakerStatus(status, prev);

        if (!status) return;

        const states = ["breakerOn", "breakerOff"];

        if (status.breakerOn && this.data.value !== states[0]) {
          this.data.value = states[0];
          this.$emit("save:document");
        } else if (!status.breakerOn && !states.includes(this.data.value as string)) {
          this.data.value = states[1];
          this.$emit("save:document");
        }
      }
    }
  },

  methods: {
    logBreakerStatus(val: BreakerStatus, prev: BreakerStatus) {
      if (!val || !prev) return;

      let status;

      if (!prev.breakerOn && val.breakerOn) {
        status = "ON";
      } else if (prev.breakerOn && !val.breakerOn) {
        status = "OFF";
      }

      if (status) {
        this.$store.dispatch(
          "track",
          `breaker status now ${status} for meter s/n: ${this.module.data?.meterSerialNumbers?.[0]?.serialNumber} for installation: ${this.installation.id}`
        );
      }
    },

    async checkBreakerStatus() {
      window.clearTimeout(this.breakerStatusTimeout!);

      if (this.hasAssociation) {
        this.breakerStatus = await this.$store.dispatch(
          "installation/meter:breakerStatus",
          this.installation.id
        );
      }

      this.breakerStatusTimeout = window.setTimeout(() => this.checkBreakerStatus(), 10 * 1000);
    },
    connect(): void {
      if (!this.readyForAssociation) return;

      this.$store
        .dispatch("installation/meter:connect", {
          id: this.installation.id,
          type: this.module.type
        })
        .catch(shownInStatus);

      this.showAlert = true;
    },

    disconnect(): void {
      this.unregisterWarning = !this.unregisterWarning;
      // if (!this.readyForAssociation) return;

      this.$store
        .dispatch("installation/meter:disconnect", {
          id: this.installation.id,
          type: this.module.type
        })
        .catch(shownInStatus);

      this.showAlert = true;
    },

    testMeter() {
      if (!this.readyForProductionTest) return;

      this.$store
        .dispatch("installation/meter:test", {
          id: this.installation.id,
          type: this.module.type
        })
        .catch(shownInStatus);

      this.showAlert = true;
    },

    async toggleBreaker() {
      if (!this.readyForProductionTest) return;

      try {
        await this.$store.dispatch("installation/toggle:breaker", {
          id: this.installation.id,
          type: this.module.type
        });

        this.checkBreakerStatus();
      } catch (e) {
        this.data.value = "error";
        this.$emit("save:document");
      }
    }
  }
});
